import { useMutation } from "@tanstack/react-query";
import { Alert, Button, Form, Input, Modal, Select, Space, Typography } from "antd";
import { useState } from "react";
import { api, unwrap } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useI18n } from "@/i18n";
import type { Product } from "./Products";

export type AiRequest = { connectionId?: string; fix?: boolean };

/**
 * Bantu tulis dengan AI: a draft of the product's name and description from
 * what Zerpid knows of it (the store's category and attributes, the seller's
 * notes, or what the marketplace asked to correct). The draft goes into the
 * editor's form; nothing is saved until the seller saves the product.
 * Mounted only while open, so each opening starts clean.
 */
export function AiCopyModal({ product, request, onApply, onClose }: {
  product: Product;
  request: AiRequest;
  onApply: (name: string, description: string) => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const errorText = useErrorText();
  const [connectionId, setConnectionId] = useState(request.connectionId);
  const [notes, setNotes] = useState("");
  const [draft, setDraft] = useState<{ name: string; description: string; changes: string } | null>(null);
  const write = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/v1/products/{productID}/copy-draft", {
          params: { path: { productID: product.id } },
          body: { connectionId, notes: notes.trim() || undefined, fix: request.fix || undefined },
        }),
      ),
    onSuccess: setDraft,
  });
  const marked = draft && /\[LENGKAPI/i.test(draft.name + draft.description);

  return (
    <Modal
      open
      title={request.fix ? t("prod.ai.fix") : t("prod.ai.title")}
      width={720}
      onCancel={onClose}
      footer={
        <Space>
          <Button onClick={() => write.mutate()} loading={write.isPending} type={draft ? "default" : "primary"}>
            {draft ? t("prod.ai.again") : t("prod.ai.generate")}
          </Button>
          <Button type="primary" disabled={!draft} onClick={() => draft && onApply(draft.name, draft.description)}>
            {t("prod.ai.use")}
          </Button>
        </Space>
      }
    >
      <Form layout="vertical">
        {request.fix ? (
          <Alert type="info" showIcon title={t("prod.ai.fixNote")} style={{ marginBottom: 16 }} />
        ) : (
          <Form.Item label={t("prod.ai.store")} extra={t("prod.ai.storeHelp")}>
            <Select
              allowClear
              value={connectionId}
              onChange={setConnectionId}
              placeholder={t("prod.ai.anyStore")}
              options={product.channels.map((c) => ({ value: c.connectionId, label: c.shopName }))}
            />
          </Form.Item>
        )}
        <Form.Item label={t("prod.ai.notes")} extra={t("prod.ai.notesHelp")}>
          <Input.TextArea rows={3} maxLength={1000} showCount value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Form.Item>
        {write.isError && <Alert type="error" showIcon title={errorText(write.error)} style={{ marginBottom: 16 }} />}
        {draft && (
          <>
            {draft.changes && <Typography.Paragraph type="secondary">{draft.changes}</Typography.Paragraph>}
            {marked && <Alert type="warning" showIcon title={t("prod.ai.placeholders")} style={{ marginBottom: 16 }} />}
            <Form.Item label={t("prod.name")}>
              <Input showCount maxLength={255} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </Form.Item>
            <Form.Item label={t("prod.description")}>
              <Input.TextArea
                autoSize={{ minRows: 8, maxRows: 18 }}
                maxLength={10000}
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
}
